import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosInstance';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { BASE_URL, ROOT_DOMAIN } from '@/config/constants';
import { toast } from 'sonner';
import { Globe, ExternalLink, Trash2, Lock, CheckCircle2, Copy, Check, ArrowRight } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { usePlan } from '@/context/PlanContext';
import CustomDomainModal from './CustomDomainModal';

/**
 * Custom Domain settings panel.
 *
 * Embedded inside the Website Settings tab. Behaviour:
 *  - Without custom-domain access → input disabled + "Upgrade" CTA opens UpgradeModal.
 *  - With access, no custom domain → input + "Connect Domain" opens the modal at STEP 1.
 *  - With access and custom domain → shows status, "Open Website" + "Remove".
 */

interface CustomDomainSettingsProps {
  domainId: string;
  slug: string;
  initialCustomDomain?: string | null;
  initialDnsVerified?: boolean;
  /** Notify the parent (SiteBrandingManager / App) when the field changes. */
  onChange?: (next: string | null) => void;
  /** Notify the parent when DNS has been verified so it can update dnsVerified in shared state. */
  onVerified?: () => void;
}

const CustomDomainSettings: React.FC<CustomDomainSettingsProps> = ({
  domainId,
  slug,
  initialCustomDomain,
  initialDnsVerified = false,
  onChange,
  onVerified,
}) => {
  const { capabilities, isSuperAdmin, openUpgradeModal } = usePlan();
  const allowed = capabilities.customDomainAccess || isSuperAdmin;

  const [customDomain, setCustomDomain] = useState<string | null>(
    initialCustomDomain ?? null,
  );
  const [dnsVerified, setDnsVerified] = useState(initialDnsVerified);
  const [removing, setRemoving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [dnsError, setDnsError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [dnsConfig, setDnsConfig] = useState<Array<{ type: string; host: string; value: string }>>([]);

  useEffect(() => {
    setCustomDomain(initialCustomDomain ?? null);
    setDnsVerified(initialDnsVerified);
  }, [initialCustomDomain, initialDnsVerified]);

  // Fetch DNS config from backend whenever a domain is set but not yet verified.
  useEffect(() => {
    if (!customDomain || dnsVerified) return;
    axios
      .get(`${BASE_URL}/api/domain/dns-config`)
      .then((r) => setDnsConfig(r.data?.records ?? []))
      .catch(() =>
        setDnsConfig([
          { type: 'A',     host: '@',   value: '76.76.21.21' },
          { type: 'CNAME', host: 'www', value: '6fc403ba9e2df09f.vercel-dns-017.com.' },
        ]),
      );

    // Ensure the domain is registered with Vercel. Covers domains that were
    // saved before the attach-on-connect fix existed (and is idempotent for the
    // rest), so an unverified domain always has a live Vercel route to resolve
    // against. Best-effort & non-blocking — verify-dns re-attaches on success.
    axios
      .post(`${BASE_URL}/api/domain/attach-vercel`, { domain: customDomain })
      .catch(() => {
        /* swallow — non-fatal */
      });
  }, [customDomain, dnsVerified]);

  const activeUrl =
    customDomain && dnsVerified
      ? `https://${customDomain}`
      : `https://${slug}.${ROOT_DOMAIN}`;

  const handleConnectClick = () => {
    if (!allowed) {
      openUpgradeModal('customDomain');
      return;
    }
    setModalOpen(true);
  };

  const handleRemove = async () => {
    if (!customDomain) return;
    setRemoving(true);
    try {
      await axios.put(`${BASE_URL}/api/domain/${domainId}`, { customDomain: null });
      setCustomDomain(null);
      setDnsVerified(false);
      onChange?.(null);
      toast.success('Custom domain removed.');
    } catch {
      toast.error('Failed to remove domain. Please try again.');
    } finally {
      setRemoving(false);
      setConfirmOpen(false);
    }
  };

  const handleSaved = (next: string) => {
    setCustomDomain(next);
    // DNS verification is re-confirmed via the modal flow; reset locally
    // and let the next prop update carry the real value from the server.
    setDnsVerified(false);
    onChange?.(next);
  };

  const handleVerified = () => {
    setDnsVerified(true);
    onVerified?.();
  };

  const copy = (value: string, key: string) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(key);
      setTimeout(() => setCopied((k) => (k === key ? null : k)), 1200);
    });
  };

  const handleInlineVerify = async () => {
    setVerifying(true);
    setDnsError(null);
    try {
      const res = await axios.get(`${BASE_URL}/api/domain/${domainId}/verify-dns`, {
        params: { _t: Date.now() },
      });
      if (res.data?.verified) {
        const ip = res.data.addresses?.[0];
        toast.success(
          ip
            ? `DNS verified ✓ — your A record resolves to ${ip}`
            : 'Domain verified — DNS is correctly configured.',
        );
        handleVerified();
      } else {
        const addrs: string[] = res.data?.addresses ?? [];
        const expectedIp = dnsConfig.find((r) => r.type === 'A')?.value || '76.76.21.21';
        if (addrs.length === 0) {
          setDnsError(
            `No A record found for ${customDomain} yet. Make sure you saved the record at your registrar — changes can take a few minutes to propagate.`,
          );
        } else {
          setDnsError(
            `Your domain is pointing to ${addrs[0]} — update the A record to ${expectedIp} at your registrar.`,
          );
        }
      }
    } catch {
      setDnsError('Could not check DNS. Please verify your connection and try again.');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <>
      <div className="bg-card rounded-lg shadow p-4 sm:p-6 space-y-4 max-w-2xl">
        <div className="flex items-start gap-3">
          <div className="rounded-md bg-primary/10 p-2">
            <Globe className="size-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-section-title">Custom Domain</h3>
            <p className="text-sm text-muted-foreground">
              Use your own domain (e.g. <span className="font-mono">yourname.com</span>)
              instead of the default subdomain.
            </p>
          </div>
        </div>

        {!allowed && (
         <div className="flex items-start gap-2 rounded-md border border-primary/20 bg-primary-light p-3 text-sm dark:bg-primary/5">
          
  <Lock className="size-4 mt-0.5 text-primary shrink-0" />
   {/* <Crown className="size-4 mt-0.5 text-primary shrink-0"/> */}
  <div className="flex-1">
    <p className="font-medium text-primary">
      Custom domains are available on the Pro plan. 
    </p>
                             
    <Button
      variant="link"
      className="px-0 h-auto text-primary font-semibold"
      onClick={() => openUpgradeModal('customDomain')}
    >
      Upgrade to unlock →
    </Button>
  </div>
</div>
        )}

        {/* Has a domain */}
        {customDomain ? (
          <div className="space-y-3">
            {/* Domain + status row */}
            <div className="rounded-md border bg-muted/30 p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-mono text-sm break-all">{customDomain}</p>
                  <p className="text-xs mt-1 flex items-center gap-1">
                    {dnsVerified ? (
                      <>
                        <CheckCircle2 className="size-3.5 text-green-600" />
                        <span className="text-green-600">DNS Verified</span>
                      </>
                    ) : (
                      <span className="text-amber-600">Pending DNS setup</span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* ── Inline DNS instructions (shown only when not yet verified) ── */}
            {!dnsVerified && (
              <div className="space-y-3">
                <div className="overflow-hidden rounded-md border">
                  <div className="px-3 py-2 bg-muted/40 border-b">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Add these DNS records at your registrar
                    </p>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="bg-muted/20 text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Type</th>
                        <th className="px-3 py-2 text-left font-medium">Host</th>
                        <th className="px-3 py-2 text-left font-medium">Value</th>
                        <th className="px-3 py-2 w-10" />
                      </tr>
                    </thead>
                    <tbody>
                      {dnsConfig.map((r) => (
                        <tr key={`${r.type}-${r.host}`} className="border-t">
                          <td className="px-3 py-2 font-mono">{r.type}</td>
                          <td className="px-3 py-2 font-mono">{r.host}</td>
                          <td className="px-3 py-2 font-mono break-all">{r.value}</td>
                          <td className="px-3 py-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => copy(r.value, `${r.type}-${r.host}`)}
                              aria-label={`Copy ${r.type} value`}
                            >
                              {copied === `${r.type}-${r.host}` ? (
                                <Check className="size-3.5 text-green-600" />
                              ) : (
                                <Copy className="size-3.5" />
                              )}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="text-xs text-muted-foreground">
                  DNS changes can take a few minutes (sometimes up to 24 hours) to propagate.
                </p>

                {dnsError && (
                  <p className="text-sm text-destructive rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2">
                    {dnsError}
                  </p>
                )}

                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={handleInlineVerify} disabled={verifying}>
                    {verifying ? (
                      <>
                      <Spinner size="sm" className="text-inherit" /> Checking DNS…
                      </>
                    ) : dnsError ? (
                      <>Check Again <ArrowRight className="size-4" /></>
                    ) : (
                      <>I&apos;ve added the records <ArrowRight className="size-4" /></>
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setConfirmOpen(true)}
                    disabled={removing}
                    className="text-destructive hover:text-destructive"
                  >
                    {removing ? (
                      <Spinner size="xs" className="text-inherit" />
                    ) : (
                      <Trash2 className="size-3.5" />
                    )}
                    Remove Domain
                  </Button>
                </div>
              </div>
            )}

            {/* ── Verified state actions ── */}
            {dnsVerified && (
              <div className="flex flex-wrap gap-2">
                <Button asChild variant="outline" size="sm">
                  <a href={activeUrl} target="_blank" rel="noopener noreferrer">
                    Open Website <ExternalLink className="size-3.5" />
                  </a>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmOpen(true)}
                  disabled={removing}
                  className="text-destructive hover:text-destructive"
                >
                  {removing ? (
                    <Spinner size="xs" className="text-inherit" />
                  ) : (
                    <Trash2 className="size-3.5" />
                  )}
                  Remove Domain
                </Button>
              </div>
            )}
          </div>
        ) : (
          /* No domain yet */
          <div className="space-y-2">
            <Button onClick={handleConnectClick} disabled={!allowed && !isSuperAdmin}>
              Connect Domain
            </Button>
            {allowed && (
              <p className="text-xs text-muted-foreground">
                We&apos;ll guide you through DNS setup after you connect.
              </p>
            )}
          </div>
        )}
      </div>

      <CustomDomainModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        domainId={domainId}
        initialCustomDomain={customDomain}
        onSaved={handleSaved}
        onVerified={handleVerified}
      />

      <Dialog open={confirmOpen} onOpenChange={(open) => !open && setConfirmOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove custom domain?</DialogTitle>
            <DialogDescription>
              <span className="font-mono">{customDomain}</span> will be disconnected from your
              site. Your site will revert to{' '}
              <span className="font-mono">{slug}.{ROOT_DOMAIN}</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 mt-4">
            <Button variant="destructive" onClick={handleRemove} disabled={removing}>
              {removing ? <Spinner size="xs" className="mr-1 text-inherit" /> : null}
              Remove
            </Button>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={removing}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CustomDomainSettings;
